"use client"

import Link from "next/link"
import { motion, useReducedMotion, useInView } from "motion/react"
import { useRef } from "react"
import { Droplets, Footprints, Shield, Wrench, Camera } from "lucide-react"
import { EASE_OUT_QUINT } from "@/lib/motion-presets"
import type { GrassCharacteristics } from "@/lib/types/recommendation-engine"

// Relocated tolerance stats — moved out of the hero when guides adopted the
// full-bleed photo hero, so the at-a-glance tolerance read stays above the
// fold (just below the hero) instead of being lost. Rendered as ds-glass
// cards to match the editorial-glass language.

function toleranceLabel(value: string | undefined): string {
  if (!value) return "Medium"
  const lower = value.toLowerCase()
  if (lower.includes("very low")) return "Very Low"
  if (lower.includes("very high")) return "Very High"
  if (lower.includes("low")) return "Low"
  if (lower.includes("high")) return "High"
  return "Medium"
}

function toleranceDots(value: string | undefined): number {
  if (!value) return 3
  const lower = value.toLowerCase()
  if (lower.includes("very low")) return 1
  if (lower.includes("very high")) return 5
  if (lower.includes("low")) return 2
  if (lower.includes("high")) return 4
  return 3
}

function StatDots({
  filled,
  total = 5,
  delayBase = 0,
}: {
  filled: number
  total?: number
  delayBase?: number
}) {
  const reduce = useReducedMotion()
  const ref = useRef<HTMLDivElement | null>(null)
  const inView = useInView(ref, { once: true, margin: "-40px" })
  return (
    <div ref={ref} className="flex gap-1">
      {Array.from({ length: total }, (_, i) => {
        const isFilled = i < filled
        const animate = reduce
          ? { backgroundColor: isFilled ? "#22c55e" : "#e5e7eb", scale: 1 }
          : inView
            ? { backgroundColor: isFilled ? "#22c55e" : "#e5e7eb", scale: 1 }
            : { backgroundColor: "#e5e7eb", scale: isFilled ? 0 : 1 }
        return (
          <motion.div
            key={i}
            className="h-2 w-2 rounded-full"
            initial={reduce ? false : { backgroundColor: "#e5e7eb", scale: isFilled ? 0 : 1 }}
            animate={animate}
            transition={{
              duration: 0.32,
              ease: EASE_OUT_QUINT,
              delay: reduce ? 0 : delayBase + (isFilled ? i * 0.06 : 0),
            }}
          />
        )
      })}
    </div>
  )
}

const statConfig = [
  {
    key: "maintenanceLevel" as const,
    label: "Maintenance",
    icon: Wrench,
    getValue: (g: GrassCharacteristics) => g.maintenanceLevel,
  },
  {
    key: "droughtTolerance" as const,
    label: "Drought Tolerance",
    icon: Droplets,
    getValue: (g: GrassCharacteristics) => g.droughtTolerance,
  },
  {
    key: "trafficTolerance" as const,
    label: "Traffic Tolerance",
    icon: Footprints,
    getValue: (g: GrassCharacteristics) => g.trafficTolerance,
  },
  {
    key: "shadeTolerance" as const,
    label: "Shade Tolerance",
    icon: Shield,
    getValue: (g: GrassCharacteristics) => g.shadeTolerance,
  },
]

interface GuideStatStripProps {
  grassData: GrassCharacteristics
}

export function GuideStatStrip({ grassData }: GuideStatStripProps) {
  return (
    <div className="mb-6 md:mb-8">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {statConfig.map((stat, i) => {
          const value = stat.getValue(grassData)
          const Icon = stat.icon
          return (
            <div key={stat.key} className="ds-glass rounded-2xl px-3 py-3">
              <div className="mb-1.5 flex items-center justify-center gap-1.5">
                <Icon className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-gray-500">{stat.label}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <StatDots filled={toleranceDots(value)} delayBase={0.18 + i * 0.05} />
                <span className="text-xs font-semibold text-gray-700">
                  {toleranceLabel(value)}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Extra at-a-glance details */}
      <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-gray-500">
        {grassData.mowingHeight && (
          <span>
            Mow at <strong className="text-gray-700">{grassData.mowingHeight}</strong>
          </span>
        )}
        {grassData.pHRange && (
          <span>
            pH <strong className="text-gray-700">{grassData.pHRange}</strong>
          </span>
        )}
        {grassData.establishmentTime && /\d\s*(?:[-–]\s*\d+\s*)?(?:days|weeks)/i.test(grassData.establishmentTime) && (
          <span>
            Germinates in{" "}
            <strong className="text-gray-700">{grassData.establishmentTime}</strong>
          </span>
        )}
      </div>

      {/* Wrong grass? helper */}
      <div className="mt-3 text-center">
        <Link
          href="/"
          className="transition-base inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-green-600"
        >
          <Camera className="h-3.5 w-3.5" />
          Not your grass type? Identify yours free
        </Link>
      </div>
    </div>
  )
}
